import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { IMAGES } from "@/config/siteConfig";
import { scrollToId } from "@/lib/scroll";
import { MaskedLine, FadeIn } from "@/components/Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative h-[80vh] min-h-[560px] max-h-[900px] overflow-hidden bg-navy" data-testid="hero-section">
      <motion.img
        src={IMAGES.hero}
        alt="Premium modern villa exterior"
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 w-full h-full object-cover will-change-transform"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/45 to-navy/15" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/20" />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 h-full max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center"
      >
        <FadeIn delay={0.15}>
          <span className="inline-flex items-center gap-3 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold" /> RK Real Estate
          </span>
        </FadeIn>

        <h1 className="mt-6 text-white font-semibold leading-[1.08] tracking-tight text-[2.5rem] sm:text-6xl lg:text-[4.4rem] max-w-3xl">
          <MaskedLine delay={0.35}>Find a Place</MaskedLine>
          <MaskedLine delay={0.5}>
            You&rsquo;ll <em className="italic font-medium text-gold">Love</em> to
          </MaskedLine>
          <MaskedLine delay={0.65}>
            Call Home<span className="text-gold">.</span>
          </MaskedLine>
        </h1>

        <FadeIn delay={0.95} className="mt-6 max-w-xl">
          <p className="text-white/75 text-sm sm:text-base lg:text-lg leading-relaxed">
            Discover residential, commercial and rental properties that fit your needs, lifestyle and budget.
          </p>
        </FadeIn>

        <FadeIn delay={1.15} className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-md sm:max-w-none">
          <button
            onClick={() => scrollToId("#properties")}
            data-testid="hero-explore-properties-button"
            className="bg-gold text-navy hover:bg-gold-dark hover:-translate-y-0.5 transition-all duration-300 rounded-lg px-7 py-4 text-[15px] font-semibold flex items-center justify-center gap-2 shadow-lg shadow-gold/25"
          >
            Explore Properties <ArrowRight size={17} />
          </button>
          <button
            onClick={() => scrollToId("#enquire")}
            data-testid="hero-talk-to-expert-button"
            className="bg-white/10 backdrop-blur-sm border border-white/40 text-white hover:bg-white/20 hover:-translate-y-0.5 transition-all duration-300 rounded-lg px-7 py-4 text-[15px] font-semibold flex items-center justify-center gap-2"
          >
            Talk to a Property Expert
          </button>
        </FadeIn>
      </motion.div>

      <FadeIn delay={1.6} className="absolute bottom-24 sm:bottom-28 left-1/2 -translate-x-1/2 z-10 hidden sm:block">
        <ChevronDown className="text-white/60 scroll-cue" size={26} />
      </FadeIn>
    </section>
  );
};
